// ─── Yonder Affiliate Service ─────────────────────────────────────────────────
//
// Handles all affiliate network link construction and click tracking.
//
// Networks supported (Phase 1 — Cloudflare web):
//   Booking.com     Hotels worldwide         ~4% revenue share
//   GetYourGuide    Activities & experiences  ~8% revenue share
//   Skyscanner      Flight search             ~50p–£1 CPC
//   Viator          Tours & activities        ~8% revenue share
//   Jet2holidays    UK package holidays       ~1.5% revenue share (family focus)
//   TUI             UK package holidays       ~1% revenue share (family focus)
//   Klook           Asia-Pacific activities   ~6% revenue share
//   RentalCars.com  Car hire                 ~5% revenue share
//   Airbnb          Accommodation            £25–£75 CPA per new booking
//   Direct          Agreed flat CPA with merchant outside a network
//
// Phase 2 (Azure + authenticated users):
//   - Server-side click logging to DB with user ID for attribution
//   - Real-time commission tracking dashboard
//   - A/B test which network converts better per destination
//
// Revenue model note:
//   Yonder earns when a user clicks through AND completes a booking.
//   Booking.com 30-day cookie, GYG 30-day, Skyscanner session-based.
//   All links are clearly labelled — no dark patterns.

import type { Deal, AffiliateNetwork, AffiliateClickEvent, DnaKey } from '@/types'

// ─── Affiliate Network Configurations ────────────────────────────────────────
// Partner IDs come from env vars — set these in .env.local and Cloudflare secrets

interface NetworkConfig {
  name: string
  paramKey: string           // URL parameter name for the partner/affiliate ID
  paramValue: () => string   // Reads from env at call time
  utmSource: string
  cookieDays: number
  signupUrl: string          // Where to register as an affiliate partner
}

const NETWORK_CONFIGS: Record<AffiliateNetwork, NetworkConfig> = {
  booking_com: {
    name: 'Booking.com',
    paramKey: 'aid',
    paramValue: () => import.meta.env.VITE_BOOKING_COM_AID || 'REPLACE_WITH_AID',
    utmSource: 'yonder_family',
    cookieDays: 30,
    signupUrl: 'https://www.booking.com/affiliate-program.html',
  },
  get_your_guide: {
    name: 'GetYourGuide',
    paramKey: 'partner_id',
    paramValue: () => import.meta.env.VITE_GYG_PARTNER_ID || 'REPLACE_WITH_PARTNER_ID',
    utmSource: 'yonder',
    cookieDays: 30,
    signupUrl: 'https://partner.getyourguide.com/',
  },
  skyscanner: {
    name: 'Skyscanner',
    paramKey: 'associateid',
    paramValue: () => import.meta.env.VITE_SKYSCANNER_PARTNER_ID || 'REPLACE_WITH_PARTNER_ID',
    utmSource: 'yonder_family',
    cookieDays: 1, // session-based for flights
    signupUrl: 'https://www.partners.skyscanner.net/',
  },
  viator: {
    name: 'Viator',
    paramKey: 'pid',
    paramValue: () => import.meta.env.VITE_VIATOR_PARTNER_ID || 'REPLACE_WITH_PID',
    utmSource: 'yonder',
    cookieDays: 30,
    signupUrl: 'https://www.viator.com/partner',
  },
  airbnb: {
    name: 'Airbnb',
    paramKey: 'c',  // via Impact Radius
    paramValue: () => import.meta.env.VITE_AIRBNB_PARTNER_ID || 'REPLACE_WITH_ID',
    utmSource: 'yonder_family',
    cookieDays: 30,
    signupUrl: 'https://www.airbnb.co.uk/associates',
  },
  jet2: {
    name: 'Jet2holidays',
    paramKey: 'affid',
    paramValue: () => import.meta.env.VITE_JET2_PARTNER_ID || 'REPLACE_WITH_AFFID',
    utmSource: 'yonder_family',
    cookieDays: 30,
    signupUrl: 'https://www.jet2holidays.com/affiliate',
  },
  tui: {
    name: 'TUI',
    paramKey: 'affiliate',
    paramValue: () => import.meta.env.VITE_TUI_PARTNER_ID || 'REPLACE_WITH_ID',
    utmSource: 'yonder_family',
    cookieDays: 30,
    signupUrl: 'https://www.tui.co.uk/destinations/affiliate-programme',
  },
  klook: {
    name: 'Klook',
    paramKey: 'aid',
    paramValue: () => import.meta.env.VITE_KLOOK_PARTNER_ID || 'REPLACE_WITH_AID',
    utmSource: 'yonder',
    cookieDays: 30,
    signupUrl: 'https://affiliate.klook.com/',
  },
  rentalcars: {
    name: 'RentalCars.com',
    paramKey: 'affiliateCode',
    paramValue: () => import.meta.env.VITE_RENTALCARS_PARTNER_ID || 'REPLACE_WITH_CODE',
    utmSource: 'yonder_family',
    cookieDays: 30,
    signupUrl: 'https://www.rentalcars.com/partners/',
  },
  direct: {
    name: 'Direct Partner',
    paramKey: 'ref',
    paramValue: () => 'yonder',
    utmSource: 'yonder_family',
    cookieDays: 30,
    signupUrl: '',
  },
}

// ─── Session ID (for click attribution without requiring login in Phase 1) ────

function getSessionId(): string {
  const key = 'yonder_session_id'
  let id = sessionStorage.getItem(key)
  if (!id) {
    id = `ys_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
    sessionStorage.setItem(key, id)
  }
  return id
}

// ─── Build Affiliate Link ─────────────────────────────────────────────────────

export function buildAffiliateLink(deal: Deal): string {
  // If a hard-coded override URL exists (direct deal), use it as-is
  if (deal.overrideUrl) return deal.overrideUrl

  const config = NETWORK_CONFIGS[deal.network]
  if (!config) return deal.baseUrl

  try {
    const url = new URL(deal.baseUrl)

    // Add affiliate/partner ID
    url.searchParams.set(config.paramKey, config.paramValue())

    // Add UTM tracking so Google Analytics / Cloudflare Analytics attributes revenue
    url.searchParams.set('utm_source', config.utmSource)
    url.searchParams.set('utm_medium', 'affiliate')
    url.searchParams.set('utm_campaign', `yonder_family_${deal.category}`)
    url.searchParams.set('utm_content', deal.title.toLowerCase().replace(/\s+/g, '_').slice(0, 40))

    // Add Yonder session ID for Phase 1 attribution (no login required)
    url.searchParams.set('yonder_sid', getSessionId())

    return url.toString()
  } catch {
    // Malformed baseUrl — return as-is
    return deal.baseUrl
  }
}

// ─── Track Click ──────────────────────────────────────────────────────────────
// Phase 1: logs to console + sessionStorage for client-side visibility
// Phase 2: POST to /api/affiliate/click with user ID for server-side attribution

export function trackAffiliateClick(
  deal: Deal,
  destination: string,
  dnaType: string
): void {
  const event: AffiliateClickEvent = {
    dealTitle: deal.title,
    network: deal.network,
    category: deal.category,
    destination,
    dnaType,
    commissionModel: deal.commissionModel,
    commissionValue: deal.commissionValue,
    url: buildAffiliateLink(deal),
    timestamp: new Date().toISOString(),
    sessionId: getSessionId(),
  }

  // Phase 1: client-side session log
  const existingLog = JSON.parse(sessionStorage.getItem('yonder_affiliate_clicks') || '[]')
  existingLog.push(event)
  sessionStorage.setItem('yonder_affiliate_clicks', JSON.stringify(existingLog))

  // Console output in dev
  if (import.meta.env.VITE_APP_ENV === 'development') {
    console.info('[Yonder Affiliate]', event)
  }

  // Phase 2 hook — replace with fetch('/api/affiliate/click', { method:'POST', body: JSON.stringify(event) })
  // when the FastAPI backend is live
}

// ─── Open Deal Link ───────────────────────────────────────────────────────────
// Called from the DealCard component — builds link, tracks click, opens in new tab

export function openDeal(deal: Deal, destination: string, dnaType: string): void {
  trackAffiliateClick(deal, destination, dnaType)
  const url = buildAffiliateLink(deal)
  window.open(url, '_blank', 'noopener,noreferrer')
}

// ─── Revenue Estimation (shown in dev/admin mode) ─────────────────────────────
// Useful for investor reporting and for the Phase 2 dashboard

export function estimateMonthlyRevenue(
  deals: Deal[],
  clickThroughRate: number = 0.18,   // 18% CTR on deals shown — conservative for families
  conversionRate: number = 0.06,     // 6% of clicks convert to bookings — industry avg
  avgBookingValue: number = 1200,    // £1,200 average family holiday spend
  monthlyActiveUsers: number = 1000
): {
  estimatedClicks: number
  estimatedBookings: number
  estimatedRevenue: number
  breakdown: { title: string; network: string; estimatedEarning: number }[]
} {
  const estimatedClicks = Math.round(monthlyActiveUsers * deals.length * clickThroughRate)
  const estimatedBookings = Math.round(estimatedClicks * conversionRate)

  const breakdown = deals.map((deal) => {
    const clicks = Math.round(monthlyActiveUsers * clickThroughRate)
    const bookings = Math.round(clicks * conversionRate)
    let earning = 0

    switch (deal.commissionModel) {
      case 'cpa':
        earning = bookings * deal.commissionValue
        break
      case 'cpc':
        earning = clicks * deal.commissionValue
        break
      case 'revenue_share':
        earning = bookings * avgBookingValue * deal.commissionValue
        break
    }

    return { title: deal.title, network: deal.network, estimatedEarning: Math.round(earning) }
  })

  return {
    estimatedClicks,
    estimatedBookings,
    estimatedRevenue: breakdown.reduce((sum, d) => sum + d.estimatedEarning, 0),
    breakdown,
  }
}

// ─── Get network display name ─────────────────────────────────────────────────

export function getNetworkLabel(network: AffiliateNetwork): string {
  return NETWORK_CONFIGS[network]?.name ?? network
}

// ─── Get network brand colour ─────────────────────────────────────────────────

export const NETWORK_COLORS: Record<AffiliateNetwork, string> = {
  booking_com:    '#003580',
  get_your_guide: '#FF5533',
  skyscanner:     '#0770E3',
  viator:         '#142B51',
  airbnb:         '#FF5A5F',
  jet2:           '#E8041D',
  tui:            '#E2001A',
  klook:          '#FF5722',
  rentalcars:     '#004B87',
  direct:         '#06D6A0',
}
